import { CommonModule, NgIf, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OutboxService } from 'src/app/shared/services/outbox/outbox.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LtmsDataTableV2Component } from 'src/app/shared/components/ltms-data-table-v2/ltms-data-table-v2.component';
import { FileUploadService } from 'src/app/shared/services/file-upload/file-upload.service';
import { SettingsService } from 'src/app/shared/services/settings/settings.service';

@Component({
  selector: 'app-outbox-afp-configuration',
  standalone: true,
  templateUrl: './outbox-afp-configuration.component.html',
  styleUrls: ['./outbox-afp-configuration.component.scss'],
  imports: [CommonModule, FormsModule, NgIf, NgStyle, LtmsDataTableV2Component]
})
export class OutboxAfpConfigurationComponent implements OnInit {
  @Input() brandId: string | null = null;
  @Input() isAllowedDeleteAFPConfig: boolean = true; // needs to be changed to true to enable delete button
  @Output() deleted = new EventEmitter<void>();

  isLoading: boolean = false;
  isError: boolean = false;
  errorMsg: string = '';

  afpConfigs: any = {};
  afpConfigKeys: string[] = [];
  selectedConfigName: string = '';
  selectedConfigDetails: any = null;
  brandDetailsLoaded = false;
  isWriteAllowed = false;
  isDeleteDisabled: boolean = true;
  tableRows: Array<{ columnName: string; mappedId: string; type: string }>=[];
  tableHeaders: any[] = [
    {
      header: 'Column Name',
      dataKey: 'columnName',
      dataType: 'string',
      order: 1,
      sortingEnabled: false,
      enabled: true,
      style: { headerTextAlign: 'left', cellTextAlign: 'left', width: '34%' }
    },
    {
      header: 'Mapped Id',
      dataKey: 'mappedId',
      dataType: 'string',
      order: 2,
      sortingEnabled: false,
      enabled: true,
      style: { headerTextAlign: 'left', cellTextAlign: 'left', width: '33%' }
    },
    {
      header: 'Type',
      dataKey: 'type',
      dataType: 'string',
      order: 3,
      sortingEnabled: false,
      enabled: true,
      style: { headerTextAlign: 'left', cellTextAlign: 'left', width: '33%' }
    }
  ];
  isDataLoaded: boolean = true;
  // delete confirmation modal
  showDeleteModal: boolean = false;
  deleteModalHeader: string = 'Delete Configuration (Critical)';
  deleteModalBody: string = '';

  constructor(
    private outboxService: OutboxService,
    private toast: ToastService,
    private settingsService: SettingsService
  ) { }

  ngOnInit(): void {
    this.fetchConfigs();
  }

  fetchConfigs(): void {
    if (!this.brandId) return;
    this.isLoading = true;
    this.isError = false;
    this.errorMsg = '';
    this.outboxService.getAfpConfigs(this.brandId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const configs = res?.data?.afp_configs || {};
        this.afpConfigs = configs;
        this.afpConfigKeys = Object.keys(configs || {});
        if (this.afpConfigKeys.length === 0) {
          this.selectedConfigName = '';
          this.selectedConfigDetails = null;
        } else if (this.selectedConfigName) {
          this.onSelectConfig(this.selectedConfigName);
        }
      },
      error: () => {
        this.isLoading = false;
        this.isError = true;
        this.errorMsg = 'Error fetching AFP configurations';
      }
    })
  }

  onSelectConfig(name: string): void {
    this.selectedConfigName = name;
    this.selectedConfigDetails = this.afpConfigs?.[name] || null;
    this.buildTableData();
  }

  openDeleteModal(): void {
    if (!this.selectedConfigName) return;
    this.deleteModalBody = `Are you sure you want to delete this configuration? "${this.selectedConfigName}"?`;
    this.showDeleteModal = true;
  }

  proceedDelete(): void {
    this.showDeleteModal = false;
    this.deleteConfig(this.selectedConfigName);
  }

  cancelDelete(): void {
    this.showDeleteModal = false;
  }

  private deleteConfig(configName: string): void {
  
    if (!this.brandId) {
      this.toast.showPopup(
        'ERROR',
        'Missing Brand',
        'Brand ID is missing. Please try again or reload the page.',
        5000
      );
      return;
    }
  
    if (!configName) {
      this.toast.showPopup(
        'ERROR',
        'Missing Config',
        'No configuration selected to delete.',
        5000
      );
      return;
    }
  
    // Prepare request payload
    const type = this.selectedConfigDetails?.type || this.selectedConfigDetails?.config_type || undefined;
    const requestData: any = {
      config_name: configName,
      brand_id: this.brandId
    };
    if (type) requestData.type = type;
  
    // Send request
    this.settingsService.deleteMappingAFPConfig(requestData).subscribe({
      next: (res: any) => {
        const code = res?.code?.toString() || res?.data?.code?.toString() || '';
        const errorMessage = res?.error_message || res?.data?.error_message;
  
        switch (code) {
          case '0':
            this.toast.showPopup('SUCCESS', 'Deleted', 'Configuration deleted successfully.', 5000);
            this.selectedConfigName = '';
            this.selectedConfigDetails = null;
            this.fetchConfigs();
            this.deleted.emit();
            break;
  
          case '203':
            this.toast.showPopup('INFO', 'Info', errorMessage || 'Deletion was skipped for known reason.', 5000);
            break;
  
          default:
            this.toast.showPopup(
              'ERROR',
              'Delete Failed',
              errorMessage || 'Unknown error occurred while deleting the configuration.',
              5000
            );
        }
      },
  
      error: (err) => {
        const status = err?.status;
        const msg = err?.error?.message || err?.message || 'An unexpected error occurred.';
        let userMessage = 'Failed to delete configuration.';
  
        // Customize message based on HTTP status
        if (status === 0) {
          userMessage = 'Cannot reach server. Please check your network connection.';
        } else if (status >= 500) {
          userMessage = 'Server error occurred. Please try again later.';
        } else if (status === 404) {
          userMessage = 'API endpoint not found. Please contact support.';
        } else if (status === 400) {
          userMessage = 'Bad request. Please review the input and try again.';
        }
  
        this.toast.showPopup('ERROR', 'Error', `${userMessage} (${msg})`, 5000);
      }
    });
  }
  
  private buildTableData(): void {
    this.isDataLoaded = false;
    this.tableRows = [];
    const cfg = this.selectedConfigDetails;
    if (!cfg) return;
    const columns: string[] = cfg?.validations?.column_list || [];
    const mappings: Record<string,string> = cfg?.mappings || {};
    const dataTypes: Record<string,string> = cfg?.data_types || {};

    const idLabelMap: Record<string,string> = {
      name: 'Customer Name',
      userEmail: 'Customer Email Address',
      userWhatsapp: 'Customer WhatsApp Numbers (India)',
      shopId: "Touchpoint Id",
      customer_id: "Customer Id",
    };
    const typeLabelMap: Record<string,string> = {
      "date": "Saved as Dates in DD/MM/YYYY format",
      "date-iso": "Saved as Dates in YYYY-MM-DD format",
      "date-time": "Saved as DD-MM-YYYY HH:mm format",
      "date-time-iso": "Saved as YYYY-MM-DD HH:mm format",
      "time": "Saved as Time in hrs:min:sec",
      'phone_international': "Customer Phone Numbers (Rest)",
      'integer': "Customer Phone Numbers (India)"
    };

    this.tableRows = columns.map(col => {
      const mappedId = mappings?.[col] || '';
      const directType = dataTypes?.[mappedId];
      const typeById = idLabelMap[mappedId] ? idLabelMap[mappedId] : undefined;
      const finalType = typeById || (directType ? (typeLabelMap[directType] || directType) : '-');
      return { columnName: col, mappedId: mappedId || '-', type: finalType || '-' };
    });
    this.isDataLoaded = true;
  }

  resetAfpConfig(): void {
    this.selectedConfigName = '';
    this.selectedConfigDetails = null;
    this.tableRows = [];
    this.isError = false;
    this.errorMsg = '';
    this.showDeleteModal = false;
    this.deleteModalBody = '';
    this.isLoading = false; // Optionally true if you want loader
    this.isDataLoaded = true;
  }
}


